import React, { useEffect, useRef, useState } from "react"
import { useUrlParam } from "aldoo-ra/URLState"
import { ChevronDownIcon, ChevronUpIcon, MapPinIcon, TruckIcon, CreditCardIcon, CheckCircleIcon, XCircleIcon, CloudArrowDownIcon, ReceiptRefundIcon, ArrowUturnLeftIcon, ClockIcon, InboxArrowDownIcon, ExclamationTriangleIcon, BanknotesIcon, ArrowPathIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Tabs from "aldoo-ra/Tabs"
import useT from "../hooks/useT"
import useSearchParams from "../hooks/useSearchParams"
import Img from "../ui/Img"
import Chip from "../ui/Chip"
import { hasDeliverables, hasLicenseKeys, isDigitalOnlyOrder, isStoreCreditItem, isLicenseKeyItem } from "./useOrders"
import { UserOrdersPage, ReturnRequest, CancelOrderRequest, isSignedIn } from "../storeApi"
import useStoreEvents from "aldoo-ra/Store/useStoreEvents"
import ReturnRequests from "./ReturnRequests"
import CancelRequest from "./CancelRequest"
import { openOrderDocument } from "./orderDocument"
import { buildContentUrl } from "../utils/contentRoutes"
import { useProfileName } from "../hooks/useCatalogData"
import { Section, Loading, ErrorText, formatMoney } from "./ui"
import useSiteLanguage from "../hooks/useSiteLanguage"
import OfficeMapDialog from "../checkout/shipping/OfficeMapDialog"
import { officeAddress } from "../checkout/shipping/providers"
import { linePresaleOf, presaleDate } from "../utils/presale"
import { describeLine, lineChoices, lineParts } from "aldoo-ra/Store/lines"
import { showInfoToast } from "aldoo-ra/Toast"
import { addOrderToCart } from "../store/cart"
import { useCurrency } from "../store/currency"

const STATUS_CLS = {
  paid: "bg-[#ff9800] text-black",
  // placed to be paid on delivery: being prepared, its payment the courier's to collect
  processing: "bg-[#ffc107] text-black",
  shipping: "bg-[#2196f3] text-white",
  completed: "bg-[#4caf50] text-white",
  canceled: "bg-[#f44336] text-white",
  digital: "bg-[#9c27b0] text-white",
  refunded: "bg-[#8a3ab0] text-white",
  partly_refunded: "bg-[#ce93d8] text-black",
  returned: "bg-[#6b5ca5] text-white",
}
// each state with its picture: paid (waiting to be sent), on its way, done, called off, …
const STATUS_ICONS = {
  paid: CreditCardIcon,
  processing: ClockIcon,
  shipping: TruckIcon,
  completed: CheckCircleIcon,
  canceled: XCircleIcon,
  digital: CloudArrowDownIcon,
  refunded: ReceiptRefundIcon,
  partly_refunded: ReceiptRefundIcon,
  returned: ArrowUturnLeftIcon,
}

// what the courier says of the parcel, as the buyer reads it (aldoo-na/store/deliveries STATES)
const PARCEL_STATES = {
  pending: { label: "Waiting for the courier", cls: "bg-neutral text-text_secondary", icon: ClockIcon },
  in_transit: { label: "On its way", cls: "bg-[#2196f3]/15 text-[#1565c0]", icon: TruckIcon },
  ready_for_pickup: { label: "Ready for you to collect", cls: "bg-[#ff9800]/15 text-[#a35f00]", icon: InboxArrowDownIcon },
  delivered: { label: "Delivered", cls: "bg-[#4caf50] text-white", icon: CheckCircleIcon },
  returning: { label: "Returning to the seller", cls: "bg-[#f44336]/15 text-[#c62828]", icon: ArrowUturnLeftIcon },
  returned: { label: "Returned to the seller", cls: "bg-[#f44336]/15 text-[#c62828]", icon: ArrowUturnLeftIcon },
  problem: { label: "Delivery problem: contact the seller", cls: "bg-[#f44336]/15 text-[#c62828]", icon: ExclamationTriangleIcon },
}

// an open order paid on delivery is not paid yet: it reads as placed (the courier collects the money)
function OrderStatus({ status, cashOnDelivery = false }) {
  const t = useT()
  const labels = { paid: cashOnDelivery ? t`Placed` : t`Paid`, processing: t`Awaiting shipment`, shipping: t`Shipping`, completed: t`Completed`, canceled: t`Canceled`, digital: t`Digital`, refunded: t`Refunded`, partly_refunded: t`Partly refunded`, returned: t`Returned` }
  const Icon = STATUS_ICONS[status]
  return (
    <span className={`inline-flex h-[24px] items-center gap-1.5 rounded-full px-3 text-[12px] font-bold uppercase ${STATUS_CLS[status] || "bg-neutral text-text"}`} data-testid="order-status" data-status={status}>
      {Icon && <Icon className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden />}
      {labels[status] || status}
    </span>
  )
}

const CARD_BRANDS = { visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover", maestro: "Maestro" }

/** How the order is paid, for its Payment tab: in cash to the courier, or by card ("Visa •••• 4242"). */
function PaymentMethodValue({ order }) {
  const t = useT()
  if (order?.paymentMethod === "cash_on_delivery")
    return (
      <span className="inline-flex items-center gap-1.5" data-testid="order-payment-method">
        <BanknotesIcon className="w-4 h-4 shrink-0 text-success" aria-hidden />
        {order.payAtShop ? t`Pay at the shop` : t`Cash on delivery`}
      </span>
    )
  const card = order?.paymentCard
  const brand = card?.brand ? CARD_BRANDS[card.brand] || card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : null
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="order-payment-method">
      <CreditCardIcon className="w-4 h-4 shrink-0 text-text_secondary" aria-hidden />
      {[brand || t`Card`, card?.last4 && `•••• ${card.last4}`].filter(Boolean).join(" ")}
    </span>
  )
}

// the date of an order in the page's language: "Oct 1, 2026", "1 октомври 2026 г."
export const formatOrderDate = (value, language = "en") => {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value || ""
  try {
    return new Intl.DateTimeFormat(language || "en", { day: "numeric", month: language === "en" ? "short" : "long", year: "numeric" }).format(d)
  } catch {
    return new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric" }).format(d)
  }
}

// the line says whether it ships (set by the api); a line from before that
// is digital when it carries files
const isPhysicalItem = (item) => (typeof item.isPhysical === "boolean" ? item.isPhysical : !hasDeliverables(item))

function OrderItem({ item, currency }) {
  const t = useT()
  const language = useSiteLanguage()
  // bought before it was out: the delivery promised at checkout
  const presale = linePresaleOf(item)
  const contentType = useProfileName(item.profile)
  const url = item.pid ? buildContentUrl(item) : undefined
  const thumb = <Img src={item.square || item.thumb} alt={item.name} width={44} height={44} className="w-11 h-11 rounded-lg overflow-hidden shrink-0" />
  const choices = lineChoices(item)
  const parts = lineParts(item)
  return (
    <li className="flex items-center gap-3 py-2">
      {url ? <Button variant="none" href={url}>{thumb}</Button> : thumb}
      <div className="flex-1 min-w-0 flex flex-col">
        {url ? (
          <Button variant="none" href={url} className="font-semibold truncate hover:underline">
            {item.name}
          </Button>
        ) : (
          <span className="font-semibold truncate">{item.name}</span>
        )}
        {/* what was chosen on it: the size, and an outfit's parts with theirs */}
        {(choices.length > 0 || parts.length > 0) && (
          <span className="text-[13px] text-text_secondary flex flex-col" data-testid="order-item-choices">
            {choices.length > 0 && <span>{choices.join(", ")}</span>}
            {parts.map((part) => (
              <span key={part}>{part}</span>
            ))}
          </span>
        )}
        <span className="text-[13px] text-text_secondary truncate">
          {item.author_display_name || item.publisher || contentType || ""}
          {item.quantity > 1 ? ` · ×${item.quantity}` : ""}
          {/* some of the line went back: what is listed is what is left */}
          {item.refundedQuantity > 0 ? ` · ${t("%count% returned", item.refundedQuantity)}` : ""}
        </span>
        {presale && (
          <span className="text-[12px] text-info font-semibold" data-testid="order-item-presale">
            {presale.deliverBy ? t("Pre-sale · delivered by %date%", presaleDate(presale.deliverBy, language)) : t`Pre-sale`}
          </span>
        )}
      </div>
      <span className="shrink-0 text-[14px] font-semibold whitespace-nowrap">{formatMoney((item.price || 0) * (item.quantity || 1), currency)}</span>
    </li>
  )
}

function Order({ order, expanded, dimmed = false, onToggle, onChanged }) {
  const t = useT()
  const [invoiceError, setInvoiceError] = useState(null)
  // money given back is the later fact, so it has the last word; a
  // digital-only order is delivered the moment it is paid: completed, like a parcel that arrived
  const refunds = order.refunds || []
  // the refunds of one day together: the day, its sum, its refunds and their (different) reasons
  const refundDays = refunds.reduce((days, refund) => {
    const key = refund.at ? new Date(refund.at).toDateString() : ""
    let day = days.find((entry) => entry.key === key)
    if (!day) days.push((day = { key, at: refund.at, amount: 0, refunds: [], reasons: [] }))
    day.amount += refund.amount || 0
    day.refunds.push(refund)
    if (refund.reason && !day.reasons.includes(refund.reason)) day.reasons.push(refund.reason)
    return days
  }, [])
  const refundState = order.refundState || (refunds.length ? "partial" : "none")
  // all of it given back ends the order; a part of it does not — what is left
  // still goes out, so the order says how far it got (the refunds tab says the rest)
  const status = refundState === "full" ? "refunded" : isDigitalOnlyOrder(order) ? "completed" : order.orderStatus || "paid"
  // what the order came to after its refunds (the api's netOf totals)
  const refunded = order.totals?.refunded ?? 0
  const net = order.totals?.net ?? order.amount
  // where it goes: the api's resolved address, or whatever an older answer carried
  const address = order.address || order.shippingAddress || order.billingAddress
  const physical = order.items.some(isPhysicalItem)
  // how it travels, as the checkout recorded it: the courier and the method,
  // and for an office or a locker the place the buyer collects it from
  const provider = order.shippingProvider
  const pickupOffice = provider?.office?.name ? provider.office : null
  const [mapOpen, setMapOpen] = useState(false)
  // the details, a tab each: how it travels (physical orders) and what was paid
  // how it reached the buyer (a parcel, files, or both), and what was paid
  // nothing left to send or open (every item went back): no delivery tab
  // what is in it first (the tab it opens on), then how it travels, what was paid, what came back
  // asking to send goods back: for an order with a parcel, once it can be asked or was
  const returns = order.returns || null
  const cancellation = order.cancellation || null
  const showReturns = !!returns && (returns.canRequest || returns.requests?.length > 0)
  const tabs = [
    ["items", t`Items`],
    ...(order.items.length ? [["delivery", t`Delivery`]] : []),
    ["invoice", t`Payment`],
    ...(refunds.length ? [["refunds", t`Refunds`]] : []),
    ...(showReturns ? [["returns", t`Request for return`]] : []),
    // calling it off before it is sent: while it can be asked, or once it was
    ...(cancellation ? [["cancel", t`Cancel the order`]] : []),
  ]
  // a request still waiting for the seller: its tab carries a dot, so it is seen from the others
  const pending = {
    returns: !!returns?.requests?.some((request) => request.status === "open"),
    cancel: !!cancellation?.requests?.some((request) => request.status === "open"),
  }
  const [tab, setTab] = useState("items")
  // the seller answered: the tab says "new" — and keeps saying it while the buyer reads it,
  // though opening it already marked the answer read (it goes once they move to another tab)
  const [freshAnswer, setFreshAnswer] = useState(!!order.returns?.unseen)
  useEffect(() => {
    if (order.returns?.unseen) setFreshAnswer(true)
  }, [order.returns?.unseen])
  useEffect(() => {
    if (tab !== "returns" && !order.returns?.unseen) setFreshAnswer(false)
  }, [tab, order.returns?.unseen])
  // every opening starts on what is in the order
  useEffect(() => {
    // the seller answered something not read yet: the order opens on it
    if (expanded) setTab(returns?.unseen ? "returns" : cancellation?.unseen ? "cancel" : "items")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded])
  // the seller's answer to a cancellation opened: read
  useEffect(() => {
    if (!expanded || tab !== "cancel" || !cancellation?.unseen) return
    CancelOrderRequest({ op: "seen", order_id: order._id })
      .then((res) => res?.changed && onChanged?.())
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, tab, cancellation?.unseen])
  // the answers opened: the order stops saying there is something new
  useEffect(() => {
    if (!expanded || tab !== "returns" || !returns?.unseen) return
    ReturnRequest({ op: "seen", order_id: order._id })
      .then((res) => res?.changed && onChanged?.())
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, tab, returns?.unseen])
  const language = useSiteLanguage()
  const { currency } = useCurrency()
  // "Order again": the order's products back in the cart (a license key is bought again from its page)
  const reorderable = order.items.some((item) => !isLicenseKeyItem(item))
  const [reordering, setReordering] = useState(false)
  const reorder = async () => {
    setReordering(true)
    const added = await addOrderToCart(order.items.filter((item) => !isLicenseKeyItem(item)), { language, currency }).catch(() => 0)
    setReordering(false)
    if (!added) showInfoToast(t`These products are no longer available`, "", { enableDismissalTracking: false })
  }
  const carrierName = provider?.providerName || provider?.providerId || ""
  // the carrier is named first ("Econt · Office"): an office or a locker is the carrier's own
  const METHOD_LABELS = { address: t`Delivery to address`, delivery_to_address: t`Delivery to address`, office: t`Office`, locker: t`Locker`, pickup: t`Pickup from the shop` }
  // the seller's own shop is no carrier: the way alone
  const carrierLine = provider?.providerId === "shop" ? t`Pickup from the shop` : carrierName ? [carrierName, METHOD_LABELS[provider?.method]].filter(Boolean).join(" · ") : ""
  const digital = order.items.some((item) => !isPhysicalItem(item))
  // what was delivered, and where it is: files (a download), or access –
  // content the site plays, opened from its page (licence keys: the Licenses button)
  const digitalItems = order.items.filter((item) => !isPhysicalItem(item))
  const fileItems = digitalItems.filter(hasDeliverables)
  const accessItems = digitalItems.filter((item) => !hasDeliverables(item) && !isLicenseKeyItem(item) && item.pid)
  const licenses = hasLicenseKeys(order)

  // the order's document (account/orderDocument): offered only when there is one (order.document)
  const openInvoice = async () => {
    setInvoiceError(null)
    const response = await openOrderDocument(order._id)
    if (response?.error) setInvoiceError(t(response.error))
  }

  return (
    // closed: one line — the number, where it stands, when, what it came to;
    // a click anywhere opens it
    !expanded ? (
      <div
        data-testid="order"
        role="button"
        tabIndex={0}
        aria-expanded={false}
        onClick={onToggle}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onToggle())}
        // another order is open: this one steps back (faded), and comes forward under the pointer
        className={`group flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-5 first:pt-2 cursor-pointer transition-opacity duration-200 ${dimmed ? "opacity-40 hover:opacity-100 focus-visible:opacity-100" : ""}`}
      >
        {/* a phone: the order and its date, where it stands under them; the total and the chevron at the right */}
        <div className="sm:hidden flex items-center gap-3 w-full">
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            {/* just the number ("#09D26E8F"): with the word too, the date no longer fit beside it */}
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[15px] font-semibold" aria-label={`${t`Order`} #${order.orderId}`}>
                #{order.orderId}
              </span>
              <span className="text-[13px] text-text_secondary">{formatOrderDate(order.date, language)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <OrderStatus status={status} cashOnDelivery={order.paymentMethod === "cash_on_delivery"} />
              {(returns?.unseen || cancellation?.unseen) && (
                <span className="inline-flex h-[24px] items-center rounded-full px-3 text-[12px] font-bold bg-primary text-white">{t`Seller responded`}</span>
              )}
            </div>
          </div>
          <span className="shrink-0 text-[15px] font-bold">{formatMoney(net, order.currency)}</span>
          <ChevronDownIcon className="w-5 h-5 shrink-0 text-text_secondary" aria-hidden="true" />
        </div>
        {/* which order (its date under it), where it stands */}
        <div className="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-1">
          {/* the order, its date under it */}
          <span className="sm:w-[190px] flex flex-col">
            <span className="text-[15px] font-semibold" data-testid="order-number">
              {t`Order`} #{order.orderId}
            </span>
            <span className="text-[13px] text-text_secondary" data-testid="order-date">{formatOrderDate(order.date, language)}</span>
          </span>
          <OrderStatus status={status} cashOnDelivery={order.paymentMethod === "cash_on_delivery"} />
          {(returns?.unseen || cancellation?.unseen) && (
            <span className="inline-flex h-[24px] items-center rounded-full px-3 text-[12px] font-bold bg-primary text-white" data-testid="order-return-news">
              {t`Seller responded`}
            </span>
          )}
        </div>
        <div className="hidden sm:flex items-center gap-4">
          <span className="text-[15px] font-bold" data-testid="order-total">{formatMoney(net, order.currency)}</span>
          <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-text_secondary group-hover:text-text">
            {t`Details`}
            <ChevronDownIcon className="w-4 h-4" />
          </span>
        </div>
      </div>
    ) : (
    <div
      data-testid="order"
      className={`relative flex flex-col gap-3 transition-shadow ${
        // the open order stands out: a card of its own, lifted off the list
        expanded ? "my-3 rounded-2xl bg-paper px-5 py-6 sm:-mx-5 shadow-[0_6px_28px_rgba(0,0,0,0.10)] !border-t-transparent [&+*]:!border-t-transparent" : "py-6 first:pt-2"
      }`}
    >
      {/* Details (to close it) stays at the top right, on a phone too: the order's lines wrap beside it */}
      <div className="flex flex-row items-start sm:items-center justify-between gap-3">
        {/* a phone: as the closed row — the number and its date, where it stands under them */}
        <div className="sm:hidden flex-1 min-w-0 flex flex-col gap-1.5">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[15px] font-semibold" aria-label={`${t`Order`} #${order.orderId}`}>
              #{order.orderId}
            </span>
            <span className="text-[13px] text-text_secondary">{formatOrderDate(order.date, language)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <OrderStatus status={status} cashOnDelivery={order.paymentMethod === "cash_on_delivery"} />
            {(returns?.unseen || cancellation?.unseen) && (
              <span className="inline-flex h-[24px] items-center rounded-full px-3 text-[12px] font-bold bg-primary text-white">{t`Seller responded`}</span>
            )}
          </div>
        </div>
        <div className="hidden sm:flex flex-1 min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
          {/* the order, its date under it */}
          <span className="sm:w-[190px] flex flex-col">
            <span className="text-[15px] font-semibold" data-testid="order-number">
              {t`Order`} #{order.orderId}
            </span>
            <span className="text-[13px] text-text_secondary" data-testid="order-date">{formatOrderDate(order.date, language)}</span>
          </span>
          <OrderStatus status={status} cashOnDelivery={order.paymentMethod === "cash_on_delivery"} />
          {(returns?.unseen || cancellation?.unseen) && (
            <span className="inline-flex h-[24px] items-center rounded-full px-3 text-[12px] font-bold bg-primary text-white" data-testid="order-return-news">
              {t`Seller responded`}
            </span>
          )}
        </div>
        <div className="shrink-0 flex flex-row flex-wrap items-center justify-end gap-2">
          {licenses && (
            <Button variant="none" href={`/user/software-licenses/product-license?orderId=${order._id}`}>
              <Button variant="secondary_s" className="min-w-0">{t`Licenses`}</Button>
            </Button>
          )}
          {reorderable && (
            <Button variant="secondary_s" className="min-w-0" onClick={reorder} disabled={reordering} loading={reordering} data-testid="order-again">
              <span className="inline-flex items-center gap-1.5">
                <ArrowPathIcon className="w-4 h-4" aria-hidden="true" />
                {t`Order again`}
              </span>
            </Button>
          )}
          {/* closes it again, at the card's top right */}
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex h-[34px] items-center gap-1 rounded-full px-3 text-[13px] font-semibold text-text_secondary hover:bg-neutral"
            aria-expanded={expanded}
            data-testid="order-collapse"
          >
            {t`Details`}
            <ChevronUpIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
      <ErrorText>{invoiceError}</ErrorText>
      {expanded && (
        <div className="flex flex-col gap-4 pt-1 text-[14px]" data-testid="order-details">
          {/* the order's tabs slide sideways on a phone: "Cancel the order" broke onto two lines */}
          <Tabs
            variant="sf_underline"
            scrollable
            value={tab}
            onChange={setTab}
            tabs={tabs.map(([id, label]) => ({
              value: id,
              testId: `order-tab-${id}`,
              label: (
                <>
                  {label}
                  {id === "cancel" && cancellation?.unseen && (
                    <span className="ml-2 inline-flex h-[20px] items-center rounded-full px-2 align-middle text-[11px] font-bold uppercase bg-primary text-white" data-testid="order-tab-cancel-new">
                      {t`New`}
                    </span>
                  )}
                  {id === "returns" && (returns?.unseen || freshAnswer) && (
                    <span className="ml-2 inline-flex h-[20px] items-center rounded-full px-2 align-middle text-[11px] font-bold uppercase bg-primary text-white" data-testid="order-tab-returns-new">
                      {t`New`}
                    </span>
                  )}
                  {pending[id] && !(id === "cancel" && cancellation?.unseen) && !(id === "returns" && (returns?.unseen || freshAnswer)) && (
                    <span
                      className="ml-1.5 inline-block h-[8px] w-[8px] rounded-full bg-[#ff9800] align-super"
                      title={t`Waiting for the seller`}
                      aria-label={t`Waiting for the seller`}
                      data-testid={`order-tab-${id}-pending`}
                    />
                  )}
                </>
              ),
            }))}
          />

          {/* what the order is now: a line given back is gone, a line partly given back shows what is left */}
          {tab === "items" && (
            <div className="flex flex-col" data-testid="order-items">
              <ul className="m-0 p-0 list-none flex flex-col divide-y divide-bordered">
                {order.items.map((item, i) => (
                  <OrderItem key={`${item.pid}-${i}`} item={item} currency={order.currency} />
                ))}
              </ul>
              {!order.items.length && <p className="m-0 text-[14px] text-text_secondary" data-testid="order-all-refunded">{t`Every item in this order was refunded.`}</p>}
            </div>
          )}

          {tab === "delivery" && (
            <div className="flex flex-col gap-3" data-testid="order-delivery">
            {/* still on its way to the buyer: by when it should arrive (what checkout promised) */}
            {order.expectedDeliveryBy && ["paid", "shipping"].includes(order.orderStatus) && (
              <div className="flex items-center gap-2 rounded-lg bg-[#2196f3]/10 px-3 py-2 text-[15px]" data-testid="order-expected-delivery">
                <TruckIcon className="w-5 h-5 shrink-0 text-[#1565c0]" />
                <span>
                  {t("Expect delivery by %s", (() => {
                    try {
                      return new Date(order.expectedDeliveryBy).toLocaleDateString(language || undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Sofia" })
                    } catch {
                      return formatOrderDate(order.expectedDeliveryBy, language)
                    }
                  })())}
                </span>
              </div>
            )}
            {/* the parcel once sent: its number, where to follow it, what the courier last said */}
            {order.tracking?.number && (
              <div className="flex flex-col gap-1 rounded-lg border border-bordered p-3" data-testid="order-tracking">
                <span className="font-semibold">{t`Tracking`}</span>
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span data-testid="order-tracking-number">
                    {order.tracking.carrierName ? `${order.tracking.carrierName} · ` : ""}
                    <strong>{order.tracking.number}</strong>
                  </span>
                  {order.tracking.state && PARCEL_STATES[order.tracking.state] && (
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${PARCEL_STATES[order.tracking.state].cls}`} data-testid="order-tracking-state">
                      {React.createElement(PARCEL_STATES[order.tracking.state].icon, { className: "w-3.5 h-3.5 shrink-0", "aria-hidden": true })}
                      {t(PARCEL_STATES[order.tracking.state].label)}
                    </span>
                  )}
                  {order.tracking.url && (
                    <a href={order.tracking.url} target="_blank" rel="noreferrer" className="text-[14px] font-semibold text-primary underline" data-testid="order-tracking-link">
                      {t`Track the parcel`}
                    </a>
                  )}
                </span>
                {(order.tracking.deliveredAt || order.deliveredAt) && order.orderStatus === "completed" && (
                  <span className="text-[13px] text-text_secondary">{t("Delivered on %s", formatOrderDate(order.tracking.deliveredAt || order.deliveredAt, language))}</span>
                )}
              </div>
            )}
            {physical && pickupOffice && (
              <>
              {/* who collects it, first: the name the office asks for, the phone the code goes to */}
              {address && (
                <div className="flex flex-col" data-testid="order-recipient">
                  <span className="font-semibold">{t`Recipient`}</span>
                  <span className="text-text_secondary">{[address.name, address.phone].filter(Boolean).join(", ")}</span>
                  {pickupOffice.type === "locker" && <span className="text-[13px] text-text_secondary">{t`The locker code is sent to this phone when the parcel arrives.`}</span>}
                </div>
              )}
              <div className="flex flex-col gap-1" data-testid="order-pickup">
                <span className="font-semibold">{pickupOffice.type === "shop" ? t`Collect from the shop` : pickupOffice.type === "locker" ? t`Collect from locker` : t`Collect from office`}</span>
                <span className="flex items-start gap-2">
                  <MapPinIcon className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                  <span className="flex flex-col">
                    <span className="font-semibold">{[pickupOffice.type === "shop" ? null : carrierName, pickupOffice.name].filter(Boolean).join(" · ")}</span>
                    {pickupOffice.address && <span className="text-text_secondary">{officeAddress(pickupOffice)}</span>}
                    {pickupOffice.type === "shop" && (pickupOffice.phone || pickupOffice.hours) && <span className="text-text_secondary">{[pickupOffice.phone, pickupOffice.hours].filter(Boolean).join(" · ")}</span>}
                    <button
                      type="button"
                      onClick={() => setMapOpen(true)}
                      className="self-start mt-1.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-bordered text-[13px] font-semibold text-text hover:border-primary hover:text-primary"
                      data-testid="order-office-map"
                    >
                      <MapPinIcon className="w-4 h-4" />
                      {t`Show on map`}
                    </button>
                  </span>
                </span>
                <OfficeMapDialog isOpen={mapOpen} onClose={() => setMapOpen(false)} providerId={provider?.providerId} office={pickupOffice} language={language} />
              </div>
              </>
            )}
            {physical && address && !pickupOffice && (
              <div className="flex flex-col" data-testid="order-shipping-address">
                {carrierLine && <span className="text-text_secondary">{carrierLine}</span>}
                <span className="font-semibold">{t`Shipping Address`}</span>
                <span className="text-text_secondary">
                  {[address.name, address.line1 || address.street, address.line2, [address.postal_code || address.postalCode, address.city].filter(Boolean).join(" "), address.state, address.country].filter(Boolean).join(", ")}
                </span>
                {address.phone && <span className="text-text_secondary">{address.phone}</span>}
              </div>
            )}
              {digital && (
                <div className="flex flex-col gap-3" data-testid="order-digital">
                  <div>
                    <Chip tone="neutral" size="sm" label={t`Digital delivery`} className="text-[11px] uppercase tracking-wide" />
                  </div>
                  {fileItems.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-text_secondary">{t`The files are in your account.`}</span>
                      <Button variant="secondary_s" href="/user/digital-products" className="min-w-0">{t`Files`}</Button>
                    </div>
                  )}
                  {/* content the site plays: opened from its own page (the "Open" button there) */}
                  {accessItems.map((item) => (
                    <div key={item.lineKey || item.pid} className="flex items-center gap-3" data-testid="order-digital-access">
                      <Button variant="secondary_s" href={buildContentUrl(item)} className="min-w-0 shrink-0">
                        {t`Open`}
                      </Button>
                      <Img src={item.square || item.thumb} alt={item.name} width={44} height={44} className="w-11 h-11 rounded-lg overflow-hidden shrink-0" />
                      <span className="min-w-0 truncate font-semibold">{item.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "invoice" && (
            <div className="flex flex-col gap-3" data-testid="order-invoice">
              <dl className="m-0 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 max-w-[420px]">
                {/* how it is paid: in cash to the courier, or the card */}
                <dt className="text-text_secondary">{t`Payment method`}</dt>
                <dd className="m-0 text-right"><PaymentMethodValue order={order} /></dd>
                <dt className="text-text_secondary">{t`Products`}</dt>
                <dd className="m-0 text-right">{formatMoney(Math.max(0, (order.amount || 0) - (order.shippingCost || 0)), order.currency)}</dd>
                {physical && (
                  <>
                    <dt className="text-text_secondary">{t`Shipping`}</dt>
                    <dd className="m-0 text-right">{order.shippingCost > 0 ? formatMoney(order.shippingCost, order.currency) : t`Free`}</dd>
                  </>
                )}
                {/* the rule sits over the line the order comes to: the total, or
                    with refunds the total after them */}
                {!(refunded > 0) && <div className="col-span-2 my-1.5 border-t border-bordered" aria-hidden="true" data-testid="order-invoice-rule" />}
                {/* refunded: what was paid, what came back, and what it came to */}
                <dt className="font-bold">{refunded > 0 ? t`Amount paid` : t`Total`}</dt>
                <dd className="m-0 text-right font-bold">{formatMoney(order.amount, order.currency)}</dd>
                {refunded > 0 && (
                  <>
                    <dt className="text-text_secondary">{t`Refunded`}</dt>
                    <dd className="m-0 text-right" data-testid="order-refunded">−{formatMoney(refunded, order.currency)}</dd>
                    <div className="col-span-2 my-1.5 border-t border-bordered" aria-hidden="true" data-testid="order-invoice-rule" />
                    <dt className="font-bold">{t`Total after refunds`}</dt>
                    <dd className="m-0 text-right font-bold" data-testid="order-net">{formatMoney(net, order.currency)}</dd>
                  </>
                )}
              </dl>
              {order.invoiceDetails?.companyName && (
                <div className="flex flex-col">
                  <span className="font-semibold">{t`Invoice to`}</span>
                  <span className="text-text_secondary">
                    {/* the company id and VAT number apart; an order from before
                        they were kept apart has only its one taxId */}
                    {[
                      order.invoiceDetails.companyName,
                      order.invoiceDetails.companyId && `${t`Company id`} ${order.invoiceDetails.companyId}`,
                      (order.invoiceDetails.vatNumber || (!order.invoiceDetails.companyId && order.invoiceDetails.taxId)) && `${t`VAT`} ${order.invoiceDetails.vatNumber || order.invoiceDetails.taxId}`,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              )}
              {/* the receipt, or the invoice when one was asked for: Stripe's for an order paid online,
                  the seller's for one paid on delivery — offered once it is there */}
              {order.document ? (
                <Button variant="secondary_s" className="self-start min-w-0" onClick={openInvoice} data-testid="order-invoice-download">
                  {order.document === "invoice" ? t`Download invoice` : t`Download receipt`}
                </Button>
              ) : order.invoicePending ? (
                <span className="text-[13px] text-text_secondary" data-testid="order-invoice-pending">{t`The seller issues the invoice once the order is delivered and paid.`}</span>
              ) : order.paymentMethod === "cash_on_delivery" ? (
                <span className="text-[13px] text-text_secondary" data-testid="order-invoice-cod">{order.payAtShop ? t`Paid at the shop when collected: the shop gives the receipt.` : t`Paid in cash to the courier on delivery: the courier gives the receipt.`}</span>
              ) : null}
            </div>
          )}

          {/* what came back, a day at a time: the day and its sum once, then every
              item (or sum) given back that day, and why */}
          {tab === "returns" && showReturns && <ReturnRequests order={order} onChanged={onChanged} />}
          {tab === "cancel" && cancellation && <CancelRequest order={order} onChanged={onChanged} />}

          {tab === "refunds" && (
            <ul className="m-0 p-0 list-none flex flex-col divide-y divide-bordered" data-testid="order-refunds">
              {refundDays.map((day) => (
                <li key={day.key} className="flex flex-col gap-1 py-3 first:pt-0" data-testid="order-refund">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <span className="font-semibold">{day.at ? formatOrderDate(day.at, language) : ""}</span>
                    <span className="font-semibold whitespace-nowrap">−{formatMoney(day.amount, order.currency)}</span>
                  </div>
                  {day.refunds.map((refund, index) =>
                    refund.lines?.length ? (
                      refund.lines.map((line, at) =>
                        // the delivery fee given back: a line like the goods, a truck for its picture
                        line.kind === "shipping" ? (
                          <div key={`${index}-${at}`} className="flex items-center justify-between gap-3 text-text_secondary" data-testid="order-refund-shipping">
                            <span className="flex flex-1 items-center gap-3 min-w-0">
                              <span className="w-11 h-11 rounded-lg shrink-0 flex items-center justify-center bg-neutral">
                                <TruckIcon className="w-6 h-6" />
                              </span>
                              <span className="min-w-0 break-words">{t`Delivery`}</span>
                            </span>
                            {line.amount !== null && line.amount !== undefined && <span className="shrink-0 whitespace-nowrap">{formatMoney(line.amount, order.currency)}</span>}
                          </div>
                        ) : (
                        <div key={`${index}-${at}`} className="flex items-center justify-between gap-3 text-text_secondary" data-testid="order-refund-line">
                          <span className="flex flex-1 items-center gap-3 min-w-0">
                            <Img src={line.square || line.thumb} alt={line.name} width={44} height={44} className="w-11 h-11 rounded-lg overflow-hidden shrink-0" />
                            <span className="min-w-0 break-words">
                              {line.quantity} × {describeLine(line, line.name || line.pid)}
                            </span>
                          </span>
                          {line.price !== null && line.price !== undefined && <span className="shrink-0 whitespace-nowrap">{formatMoney(line.price, order.currency)}</span>}
                        </div>
                        )
                      )
                    ) : (
                      // a sum with no items says what it was
                      <div key={index} className="flex items-center justify-between gap-3 text-text_secondary" data-testid="order-refund-line">
                        <span>{refund.mode === "full" ? t`Full amount` : t`Amount`}</span>
                        <span className="shrink-0 whitespace-nowrap">{formatMoney(refund.amount, order.currency)}</span>
                      </div>
                    )
                  )}
                  {day.reasons.map((reason) => (
                    <span key={reason} className="text-text_secondary">
                      {t`Merchant note`}: {reason}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
    )
  )
}

/**
 * The user's orders, a page at a time (user {orders} with page/search): the
 * server finds them — every word of the search in the order's number, a
 * product's name in any language or its id, or the address — and reads only
 * the page in full. ?order=<id> opens one: it is searched for, and expanded — and the order opened
 * on the page is written there (the order's number), so its link opens it again.
 */
export default function Orders() {
  const t = useT()
  const language = useSiteLanguage()
  const params = useSearchParams()
  const linked = params.get("order")
  const [opened, setOpened] = useUrlParam("order", { default: "" })
  const expanded = opened || null
  const [term, setTerm] = useState(linked || "")
  const [error, setError] = useState(null)
  // whether the buyer has any order at all: the search box is for those who do
  const [hasAny, setHasAny] = useState(null)
  // a request sent, cancelled or read: the page is read again (the open order stays open)
  const [reloads, setReloads] = useState(0)
  // the orders on the page shown: the rest fade only while the open one is among them
  const [pageIds, setPageIds] = useState([])

  // the buyer's own orders, as they change elsewhere (the seller answers, ships, refunds; the courier
  // delivers): the page is read again — a burst of notices (a refund and its order) is one reading
  const soon = useRef(null)
  useStoreEvents({
    mine: true,
    enabled: isSignedIn(),
    onEvent: () => {
      clearTimeout(soon.current)
      soon.current = setTimeout(() => setReloads((count) => count + 1), 400)
    },
  })
  useEffect(() => () => clearTimeout(soon.current), [])

  const loadPage = async ({ page, itemsPerPage, search }) => {
    const query = typeof search?.q === "string" ? search.q : ""
    const res = await UserOrdersPage({ language, page, items_per_page: itemsPerPage, search: query }).catch((e) => ({ error: e.message }))
    if (!res?.result) {
      setError(res?.error || "Request failed")
      return { data: [], total: 0 }
    }
    setError(null)
    if (!query) setHasAny(res.total > 0)
    // coins packs, gift cards and subscriptions are no order lines (useOrders does the same)
    const data = (res.orders || []).map((order) => ({ ...order, items: (order.items || []).filter((item) => !isStoreCreditItem(item)) }))
    setPageIds(data.flatMap((order) => [String(order._id), order.orderId]))
    return { data, total: res.total ?? data.length }
  }

  return (
    <Section title={t`Orders`}>
      <ErrorText>{error ? t(error) : null}</ErrorText>
      <div data-testid="orders-list">
        <DataTable
          apiMethod={loadPage}
          apiDataField="data"
          itemIdField="_id"
          refreshKey={`${language}:${reloads}`}
          itemsPerPage={10}
          searchField="q"
          // a linked order (?order=) is searched for from the first request
          initialSearchTerm={linked || ""}
          searchObject={linked ? { q: linked } : {}}
          onSearchTermChange={setTerm}
          showSearch={hasAny !== false}
          searchPlaceholder={t`Search orders by number, product or address`}
          onDataLoadingElement={<Loading />}
          // the open order is a lifted card reaching past the list's sides: no sideways-scroll fades over it
          scrollFades={false}
          paginationOfLabel={t`of`}
          renderRow={(order) => (
            <Order
              key={order._id}
              order={order}
              expanded={expanded === String(order._id) || expanded === order.orderId}
              dimmed={!!expanded && pageIds.includes(expanded) && expanded !== String(order._id) && expanded !== order.orderId}
              onToggle={() => setOpened(expanded === String(order._id) || expanded === order.orderId ? "" : order.orderId || String(order._id))}
              onChanged={() => setReloads((count) => count + 1)}
            />
          )}
          noData={
            term ? (
              <p className="m-0 py-8 text-center text-[15px] text-text_secondary">{t`No orders match`}</p>
            ) : (
              <div className="py-16 text-center">
                <p className="m-0 text-[20px] text-text_secondary">{t`No orders yet`}</p>
                <p className="m-0 mt-4 text-[14px] text-text_secondary">{t`Your order history will appear here once you make a purchase.`}</p>
              </div>
            )
          }
          styleOverrides={{
            header: "flex flex-row items-center mb-4",
            searchInput: "flex-1 w-full",
            tableWrapper: "h-auto overflow-visible",
            tbody: "flex flex-col divide-y divide-bordered",
            noDataContainer: "w-full",
            footer: "mt-4",
          }}
        />
      </div>
    </Section>
  )
}
